import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tabs,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  DesktopDateTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { ptBR } from "date-fns/locale";
import axios from "axios";
import { useMemo, useState } from "react";
import AttToken from "../../helpers/attToken";
import Anonimo from "../../service/Anonimo";
import CadastrosAgendamentosService from "../../service/CadastrosAgendamentos";
import Checklists from "../../service/Checklists";
import Empresa from "../../service/Empresa";
import Fluxo, { PendenciaAgendamento } from "../../service/Fluxo";
import queryClient from "../../service/query";
import {
  Cadastro,
  FluxoModel,
  ModeloAgendamento,
} from "../../types/fluxo/types";
import { ChecklistReportType } from "../../types/reports/types";
import GenericAutoComplete from "../GenericAutoComplete";
import ModalDetailPrint from "../Reports/Modal/ModalDetailPrint";

const fluxoService = new Fluxo();
const anonimoService = new Anonimo();
const checklistsService = new Checklists();
const cadastrosService = new CadastrosAgendamentosService();
const empresaService = new Empresa();

function mensagemErro(error: unknown) {
  return axios.isAxiosError(error) && error.response?.status === 409
    ? "Esta pendência já foi tratada por outro operador. A lista foi atualizada."
    : "Não foi possível concluir a operação. Tente novamente.";
}

function regraTexto(minimo: number, maximo: number | null) {
  if (maximo === minimo) return `Selecione exatamente ${minimo}.`;
  if (maximo === null) return `Selecione ao menos ${minimo}.`;
  return minimo
    ? `Selecione entre ${minimo} e ${maximo}.`
    : `Selecione no máximo ${maximo}.`;
}

type FormularioProps = {
  pendencia: PendenciaAgendamento;
  onCancel: () => void;
  onSubmit: (agendamento: Record<string, unknown>) => void;
  enviando: boolean;
};

function FormularioAgendamento({
  pendencia,
  onCancel,
  onSubmit,
  enviando,
}: FormularioProps) {
  const [modeloId, setModeloId] = useState("");
  const [cadastrosPorRegra, setCadastrosPorRegra] = useState<
    Record<string, string[]>
  >({});
  const [unidadeId, setUnidadeId] = useState("");
  const [dataPrevista, setDataPrevista] = useState<Date | null>(new Date());
  const modelos = useQuery({
    queryKey: ["AGENDAMENTO_MODELOS"],
    queryFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error("Token inválido");
      return cadastrosService.listarModelosAgendamento(token);
    },
    refetchOnWindowFocus: false,
  });
  const fluxos = useQuery({
    queryKey: ["FLUXO_MODELOS"],
    queryFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error("Token inválido");
      const response = await fluxoService.getAll(token);
      return response.data.data as FluxoModel[];
    },
    refetchOnWindowFocus: false,
  });
  const modelo = modelos.data?.find(
    (item: ModeloAgendamento) => item.id === modeloId,
  );
  const fluxo = fluxos.data?.find((item) => item.id === pendencia.idFluxo);
  const checklistOperacional = fluxo?.fluxo?.[0]?.checklistId;
  const regras = modelo?.regrasCadastro ?? [];
  const unidades = useQuery({
    queryKey: ["UNIDADES_FLUXO", checklistOperacional],
    enabled: Boolean(checklistOperacional),
    queryFn: async () => {
      const token = await AttToken();
      if (!token || !checklistOperacional)
        throw new Error("Unidade indisponível");
      return empresaService.getAllByFormId(token, checklistOperacional);
    },
    refetchOnWindowFocus: false,
  });
  const cadastros = useQueries({
    queries: regras.map((regra) => ({
      queryKey: ["CADASTROS", regra.idModeloCadastro],
      enabled: Boolean(modelo),
      queryFn: async () => {
        const token = await AttToken();
        if (!token) throw new Error("Token inválido");
        return cadastrosService.listarCadastros(token, regra.idModeloCadastro);
      },
      refetchOnWindowFocus: false,
    })),
  });
  const regrasAtendidas = regras.every((regra) => {
    const quantidade = cadastrosPorRegra[regra.id]?.length ?? 0;
    return (
      quantidade >= regra.minimo &&
      (regra.maximo === null || quantidade <= regra.maximo)
    );
  });
  const podeEnviar = Boolean(
    modelo &&
      fluxo &&
      checklistOperacional &&
      unidadeId &&
      dataPrevista &&
      dataPrevista.getTime() >= Date.now() &&
      regrasAtendidas,
  );

  function enviar() {
    onSubmit({
      idModeloAgendamento: modelo?.id,
      identificador: pendencia.identificador,
      cadastros: regras.flatMap((regra) =>
        (cadastrosPorRegra[regra.id] ?? []).map((idCadastro) => ({
          idRegraCadastro: regra.id,
          idCadastro,
          idModeloCadastro: regra.idModeloCadastro,
        })),
      ),
      idFluxo: pendencia.idFluxo,
      idChecklist: checklistOperacional,
      versaoFluxo: pendencia.versaoFluxo ?? fluxo?.versaoFluxo ?? 1,
      idUnidade: unidadeId,
      dataPrevista: dataPrevista?.getTime(),
      origem: "admin",
      chaveOrigem: `pendencia:${pendencia.id}`,
      payloadReferencia: {},
      empresa: modelo?.empresa,
    });
  }

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onCancel}>
      <DialogTitle>Agendar pendência</DialogTitle>
      <DialogContent>
        <Stack spacing={2} paddingTop={1}>
          {!fluxo && !fluxos.isLoading && (
            <Alert severity="error">
              O fluxo desta pendência não está mais disponível.
            </Alert>
          )}
          <TextField
            label="Fluxo"
            value={fluxo?.nome ?? pendencia.idFluxo}
            disabled
            fullWidth
          />
          <TextField
            select
            label="Modelo de agendamento"
            value={modeloId}
            onChange={(event) => {
              setModeloId(event.target.value);
              setCadastrosPorRegra({});
            }}
            disabled={modelos.isLoading}
            fullWidth
          >
            {(modelos.data ?? []).map((item: ModeloAgendamento) => (
              <MenuItem key={item.id} value={item.id}>
                {item.nome}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Unidade"
            value={unidadeId}
            onChange={(event) => setUnidadeId(event.target.value)}
            disabled={!checklistOperacional || unidades.isLoading}
            fullWidth
          >
            {(unidades.data ?? []).map(
              (item: { unidadeId: string; unidadeNome: string }) => (
                <MenuItem key={item.unidadeId} value={item.unidadeId}>
                  {item.unidadeNome}
                </MenuItem>
              ),
            )}
          </TextField>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={ptBR}
          >
            <DesktopDateTimePicker
              label="Data prevista"
              format="dd/MM/yyyy HH:mm"
              minDateTime={new Date()}
              value={dataPrevista}
              onChange={setDataPrevista}
              slotProps={{ textField: { fullWidth: true, required: true } }}
            />
          </LocalizationProvider>
          {regras.map((regra, indice) => {
            const opcoes = (cadastros[indice]?.data ?? []) as Cadastro[];
            return (
              <GenericAutoComplete
                key={regra.id}
                options={opcoes.map((item) => ({
                  label: `${item.nome} — ${item.identificador ?? item.identificadorNormalizado ?? ""}`,
                  value: item.id,
                }))}
                selectedValues={cadastrosPorRegra[regra.id] ?? []}
                loading={cadastros[indice]?.isLoading}
                onChange={(ids) =>
                  setCadastrosPorRegra((atual) => ({
                    ...atual,
                    [regra.id]: ids,
                  }))
                }
                label={regra.nome}
                helperText={regraTexto(regra.minimo, regra.maximo)}
                maxSelections={regra.maximo ?? undefined}
              />
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={!podeEnviar || enviando}
          onClick={enviar}
        >
          Agendar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AgendamentosPendentes() {
  const [subAba, setSubAba] = useState<"abertas" | "resolvidas">("abertas");
  const [cursor, setCursor] = useState<string | undefined>();
  const [selecionada, setSelecionada] = useState<PendenciaAgendamento | null>(
    null,
  );
  const [modalAgendamento, setModalAgendamento] = useState(false);
  const [modalRecusa, setModalRecusa] = useState(false);
  const [relatorioAberto, setRelatorioAberto] = useState(false);
  const [motivo, setMotivo] = useState("");

  const lista = useQuery({
    queryKey: ["PENDENCIAS_AGENDAMENTO", subAba, cursor],
    queryFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error("Token inválido");
      return fluxoService.listarPendencias(token, {
        ...(subAba === "abertas" ? { status: "aguardando_analise" } : {}),
        limit: 20,
        cursor,
      });
    },
    refetchOnWindowFocus: false,
  });

  const modelos = useQuery({
    queryKey: ["GET_SIMPLE_FORMS"],
    queryFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error("Token inválido");
      return checklistsService.getSimpleForm(token) as Promise<
        { id: string; nome: string }[]
      >;
    },
    refetchOnWindowFocus: false,
  });

  const nomes = useMemo(
    () =>
      new Map((modelos.data ?? []).map((modelo) => [modelo.id, modelo.nome])),
    [modelos.data],
  );
  const itensVisiveis = useMemo(
    () =>
      (lista.data?.items ?? []).filter((item) =>
        subAba === "abertas"
          ? item.status === "aguardando_analise"
          : item.status !== "aguardando_analise",
      ),
    [lista.data?.items, subAba],
  );

  function trocarSubAba(novaAba: "abertas" | "resolvidas") {
    setSubAba(novaAba);
    setCursor(undefined);
  }

  const detalhe = useQuery({
    queryKey: ["PENDENCIA_DETALHE", selecionada?.id],
    enabled: Boolean(selecionada),
    queryFn: async () => {
      const token = await AttToken();
      if (!token || !selecionada) throw new Error("Token inválido");
      return fluxoService.detalhePendencia(token, selecionada.id);
    },
    refetchOnWindowFocus: false,
  });

  const historico = useQuery({
    queryKey: ["PENDENCIA_HISTORICO", detalhe.data?.pendencia.id],
    enabled: Boolean(detalhe.data),
    retry: false,
    queryFn: async () => {
      const token = await AttToken();
      const detalhePendencia = detalhe.data;
      if (!token || !detalhePendencia) {
        throw new Error("Histórico indisponível");
      }
      const resposta = await anonimoService.listaHistoricoAnonimo(
        token,
        detalhePendencia.checkin.idChecklistCheckin,
        detalhePendencia.checkin.inicioHistoricoAnonimo,
        detalhePendencia.checkin.inicioHistoricoAnonimo,
      );
      const execucao = Array.isArray(resposta)
        ? resposta.find(
            (item) =>
              item.inicio === detalhePendencia.checkin.inicioHistoricoAnonimo,
          )
        : undefined;
      if (!execucao)
        throw new Error("O histórico anônimo vinculado não foi encontrado.");
      return execucao as ChecklistReportType;
    },
    refetchOnWindowFocus: false,
  });

  function fechar() {
    setSelecionada(null);
    setModalAgendamento(false);
    setModalRecusa(false);
    setRelatorioAberto(false);
    setMotivo("");
    void queryClient.invalidateQueries({
      queryKey: ["PENDENCIAS_AGENDAMENTO"],
    });
  }

  const recusar = useMutation({
    mutationFn: async () => {
      if (!selecionada) return;
      const token = await AttToken();
      if (!token) throw new Error("Token inválido");
      return fluxoService.recusarPendencia(
        token,
        selecionada.id,
        motivo.trim(),
      );
    },
    onSuccess: fechar,
    onError: (error) => alert(mensagemErro(error)),
  });
  const resolver = useMutation({
    mutationFn: async (agendamento: Record<string, unknown>) => {
      if (!selecionada) return;
      const token = await AttToken();
      if (!token) throw new Error("Token inválido");
      return fluxoService.resolverPendencia(token, selecionada.id, agendamento);
    },
    onSuccess: fechar,
    onError: (error) => alert(mensagemErro(error)),
  });
  const pendencia =
    detalhe.data && selecionada
      ? {
          ...selecionada,
          ...detalhe.data.pendencia,
          idFluxo: detalhe.data.versao.idFluxo,
          versaoFluxo: detalhe.data.versao.versaoFluxo,
          execucaoAnonimaId: detalhe.data.checkin.execucaoAnonimaId,
          idChecklistCheckin: detalhe.data.checkin.idChecklistCheckin,
          inicioHistoricoAnonimo: detalhe.data.checkin.inicioHistoricoAnonimo,
        }
      : selecionada;
  const podeAgir = detalhe.data?.pendencia.status === "aguardando_analise";

  function getChipColorByStatus(status: string): "success" | "error" | "primary" {
    if (status === 'recusado') {
      return 'error';
    } else if (status === 'agendado') {
      return 'success';
    } else {
      return 'primary';
    }
  }

  function formatStatusName(status: string | undefined): string {
    return status ? `${status.charAt(0).toUpperCase()}${status.slice(1, status.length)}`.replace("_", " ") : "-";
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h6">Agendamentos Pendentes</Typography>
      <Tabs
        value={subAba}
        onChange={(_event, novaAba) => trocarSubAba(novaAba)}
        textColor="secondary"
        indicatorColor="secondary"
      >
        <Tab value="abertas" label="Pendências abertas" />
        <Tab value="resolvidas" label="Pendências resolvidas" />
      </Tabs>
      {lista.isError && (
        <Alert severity="error">Não foi possível carregar as pendências.</Alert>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Data</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Nome</TableCell>
            <TableCell>Identificador</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {itensVisiveis.map((item) => (
            <TableRow
              key={item.id}
              hover
              sx={{ cursor: "pointer" }}
              onClick={() => setSelecionada(item)}
            >
              <TableCell>{new Date(item.criadoEm).toLocaleString()}</TableCell>
              <TableCell>
                <Chip size="small" color={getChipColorByStatus(item.status)} label={formatStatusName(item.status)} />
              </TableCell>
              <TableCell>
                {nomes.get(item.idChecklistCheckin) ?? item.idChecklistCheckin}
              </TableCell>
              <TableCell>{item.identificador}</TableCell>
            </TableRow>
          ))}
          {!lista.isLoading && !itensVisiveis.length && (
            <TableRow>
              <TableCell colSpan={4}>
                {subAba === "abertas"
                  ? "Nenhuma pendência aguardando análise."
                  : "Nenhuma pendência resolvida nesta página."}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Stack direction="row" justifyContent="space-between">
        <Button disabled={!cursor} onClick={() => setCursor(undefined)}>
          Primeira página
        </Button>
        <Button
          disabled={!lista.data?.nextCursor}
          onClick={() => setCursor(lista.data?.nextCursor)}
        >
          Próxima página
        </Button>
      </Stack>
      <Dialog
        open={Boolean(selecionada && !modalAgendamento && !modalRecusa)}
        onClose={fechar}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Detalhe da pendência</DialogTitle>
        <DialogContent>
          <Stack spacing={2} paddingTop={1}>
            {detalhe.isLoading && <Typography>Carregando...</Typography>}
            {detalhe.isError && (
              <Alert severity="error">
                Não foi possível carregar o detalhe.
              </Alert>
            )}
            {pendencia && (
              <>
                <Typography>
                  Status:{" "}
                  <strong>
                    {formatStatusName(pendencia.status)}
                  </strong>
                </Typography>
                <Typography>
                  Identificador: {pendencia.identificador}
                </Typography>
                {pendencia.motivoRecusa && (
                  <Alert severity="info">
                    Motivo da recusa: {pendencia.motivoRecusa}
                  </Alert>
                )}
              </>
            )}
            {historico.isLoading && (
              <Typography>Carregando respostas...</Typography>
            )}
            {historico.isError && (
              <Alert severity="error">
                {historico.error instanceof Error
                  ? historico.error.message
                  : "Não foi possível carregar o histórico."}
              </Alert>
            )}
            {historico.data && (
              <Button onClick={() => setRelatorioAberto(true)}>
                Ver respostas
              </Button>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={fechar}>Fechar</Button>
          {podeAgir && (
            <Button color="error" onClick={() => setModalRecusa(true)}>
              Rejeitar
            </Button>
          )}
          {podeAgir && (
            <Button
              variant="contained"
              onClick={() => setModalAgendamento(true)}
            >
              Agendar
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {historico.data && (
        <ModalDetailPrint
          openModal={relatorioAberto}
          setOpenModal={setRelatorioAberto}
          selected={[historico.data]}
        />
      )}
      {modalAgendamento && pendencia && (
        <FormularioAgendamento
          pendencia={pendencia}
          onCancel={() => setModalAgendamento(false)}
          onSubmit={(agendamento) => resolver.mutate(agendamento)}
          enviando={resolver.isPending}
        />
      )}
      <Dialog
        open={modalRecusa}
        onClose={() => setModalRecusa(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Rejeitar pendência</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Motivo"
            value={motivo}
            onChange={(event) => setMotivo(event.target.value)}
            required
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalRecusa(false)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!motivo.trim() || recusar.isPending}
            onClick={() => recusar.mutate()}
          >
            Rejeitar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
